import style from './style.module.scss';
import { useCallback, useMemo } from 'react';
import { useField } from 'formik';
import { joinNames } from '../helpers';
import { ErrorMessage } from '../Container';
import NotificationIcon from '../NotificationIcon';
import Label from '../Label';
import { useHelpBox } from '../HelpBox';
import type { BaseFieldProps, UiStateProps } from '../Input';
import useDefaultId from '@n2-common/lib/useDefaultId';
import 'rc-slider/assets/index.css';
import RcSlider from 'rc-slider';

export type SliderProps = React.SelectHTMLAttributes<HTMLSelectElement> &
    BaseFieldProps & {
        options: OptionProps<number>[] | { min: number; max: number; step?: number };
    };

export type OptionProps<T extends string | number | undefined = string> = {
    value: T;
    text: string;
};

export default function Slider({
    name,
    options,
    label,
    error,
    id,
    uistate,
    className: outerClassName,
    helptext,
    value,
    onChange,
}: SliderProps & UiStateProps) {
    const compId = useDefaultId(id);
    const errorId = `${compId}error`;
    const [HelpBox, HelpIcon] = useHelpBox(helptext, name);
    const marks = useMemo(() => (Array.isArray(options) ? options.reduce((m, { value, text }) => ({ ...m, [value]: text }), {}) : []), [options]);
    const range = useMemo(
        () => (Array.isArray(options) ? { min: Math.min(...options.map((o) => o.value)), max: Math.max(...options.map((o) => o.value)) } : options),
        [options]
    );
    const valueAsNumber = parseInt(`${value}`, 10);
    const sliderOnChange = useCallback(
        (val: number | number[]) => {
            const selected = Array.isArray(val) ? val[0] : val;
            onChange && onChange({ target: { value: selected.toString() } } as React.ChangeEvent<HTMLSelectElement>);
        },
        [onChange]
    );
    return (
        <div className={joinNames(style.root, outerClassName)}>
            <div className={style.labelWrapper}>
                <Label htmlFor={compId}>{label}</Label>
                <HelpIcon />
            </div>
            <HelpBox />
            <div
                className={joinNames(
                    style.wrapper,
                    uistate === 'value-verified' && style.valueVerified,
                    error ? style.error : undefined,
                    style.rcSlider
                )}
            >
                {Array.isArray(options) ? (
                    <RcSlider {...range} value={valueAsNumber} step={null} onChange={sliderOnChange} marks={marks} {...SLIDER_STYLES} />
                ) : (
                    <RcSlider {...range} value={valueAsNumber} step={options.step} onChange={sliderOnChange} marks={marks} {...SLIDER_STYLES}  />
                )}
                <NotificationIcon prefixForUiTesting={name} check={uistate === 'value-verified'} alert={!!error && { title: error }} />
            </div>
            {error && <ErrorMessage id={errorId}>{error}</ErrorMessage>}
        </div>
    );
}

export function FormikSlider({ name, options, label, ...attr }: SliderProps): JSX.Element {
    const [{ value, onChange }, { touched, error }, { setValue }] = useField({ name, type: 'text' });

    return (
        <>
            <input type="hidden" name={name} id={name} value={value} onChange={onChange} />
            <Slider
                name={name}
                value={value}
                options={options}
                label={label}
                uistate={'standard'}
                onChange={(e) => {
                    setValue(e.target.value);
                }}
                error={touched && error}
                {...attr}
            />
        </>
    );
}

const SLIDER_BORDER_COLOR = 'var(--color-800)';
const SLIDER_STYLES = {
    style: { margin: '12px 0' },
    dotStyle: { borderColor: SLIDER_BORDER_COLOR, bottom: '-7px', width: '12px', height: '12px' },
    activeDotStyle: {},
    railStyle: { height: '10px' },
    trackStyle: { height: '10px', background: 'var(--color-400)' },
    handleStyle: { width: '25px', height: '25px', marginTop: '-8px', borderColor: SLIDER_BORDER_COLOR },
};
